import React, {
	useCallback,
	useState,
	forwardRef,
	type ForwardedRef,
	useEffect,
	useRef,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { useRegisterNudgeTarget } from '@atlassiansox/nudge-tooltip';
import { token } from '@atlaskit/tokens';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';

import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import {
	COMPACT,
	HIDDEN,
	VISIBLE,
} from '@atlassian/jira-platform-inline-card-create/src/common/constants.tsx';
import TypeSelect, {
	type Props as TypeSelectProps,
	type SelectRef as TypeSelectRef,
} from '@atlassian/jira-platform-inline-card-create/src/ui/form/type-select/index.tsx';
import PlatformInlineIssueCreate from '@atlassian/jira-platform-inline-card-create/src/ui/main.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { SmartLinkEditorEntryPointComponent } from '@atlassian/jira-software-backlog-ai-work-creation/src/view/smart-link-editor/entrypoint.tsx';
import type { EditorApi } from '@atlassian/jira-software-backlog-ai-work-creation/src/view/smart-link-editor/ui/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import {
	BACKLOG_CREATE_ISSUE_NUDGE_ID,
	BACKLOG_CREATE_SPRINT_ISSUE_NUDGE_ID,
} from '@atlassian/jira-onboarding-core/src/constants.tsx';

import { isTailoredOnboardingScrumAndSoftwareKanbanEnabled } from '@atlassian/jira-tailored-onboarding/src/controllers/index.tsx';
import { BacklogCreateSprintIssueNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/scrum-onboarding-tour/backlog-create-sprint-issue/async.tsx';
import { BacklogCreateIssueNudgePortalledAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/scrum-onboarding-tour/backlog-create-issue-portalled/async.tsx';
import { JSW_BACKLOG_SOURCE } from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import { useAiEntitlementResource } from '@atlassian/jira-router-resources-ai-entitlement/src/services/index.tsx';

import { useStoreFields } from '../../../common/hooks/use-store-fields/main.tsx';
import { GlobalIssueCreateType } from '../../../model/modals/types.tsx';
import type { CardId, CardTypeId } from '../../../model/types.tsx';
import { cardCreateMultipleRequest } from '../../../state/actions/cards/create/index.tsx';
import { setCardIdWithAboveICCOpen } from '../../../state/actions/cards/rich-card/index.tsx';
import { setInlineCreateSessionId } from '../../../state/actions/inline-create/index.tsx';
import { openModal } from '../../../state/actions/modals/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../state/index.tsx';
import {
	getIsBoardCrossProject,
	getIsCompanyManaged,
} from '../../../state/selectors/app/index.tsx';
import { getLastAppliedParentFilters } from '../../../state/selectors/card-create/index.tsx';
import { manageIssueTypesSelector } from '../../../state/selectors/card-type/index.tsx';
import { hasICCOpen as hasICCOpenSelector } from '../../../state/selectors/cards/index.tsx';
import { getFilters } from '../../../state/selectors/filters/index.tsx';
import {
	getCardTypesForICC,
	getInlineCreateSessionId,
} from '../../../state/selectors/inline-create/index.tsx';
import { issueParentsSelector } from '../../../state/selectors/issue-parent/index.tsx';
import { canEditBoardConfig } from '../../../state/selectors/permissions/index.tsx';
import { useIsAiEnabledBacklog } from '../../../common/hooks/use-is-ai-enabled-backlog/index.tsx';
import formatMessages from './messages.tsx';
import { AiSuggestButton } from './ai-work-create/ai-suggest-button/index.tsx';
import { useAiWorkCreatePanelEntryPoint } from './ai-work-create/use-ai-work-create-panel-entrypoint/index.tsx';
import { useSelectedIssueType } from './ai-work-create/use-selected-issue-type/index.tsx';
import type { AiWorkCreatePanelEntryPointSubject } from './ai-work-create/types.tsx';
import { useIsFormVisible } from './ai-work-create/use-is-form-visible/index.tsx';
import { PlaceholderWithLinkPicker } from './ai-work-create/placeholder-with-link-picker/index.tsx';
import { HoverBox } from './ai-work-create/hover-box/index.tsx';

// Hacky way to generate optimistic issue id.
// Not ideal, but have to do it for now to react backwards compatibility with current ICC.
let lastCardId = 0;
const generateId = (): number => {
	lastCardId -= 1;
	return lastCardId;
};

export type Props = {
	isBetweenCards?: boolean;
	isOpenByDefault?: boolean;
	messages?: {
		title: string;
		description: string;
	}[];
	placeholderText?: string;
	triggerLabel?: string;
	ariaLabelledBy?: string;
	insertBefore?: CardId | null;
	listId: string;
	isFormVisible?: boolean;
	onSetAiWorkCreatePanelSubject?: (subject: AiWorkCreatePanelEntryPointSubject) => void;
	isFocusMovedToAiPanel?: () => boolean;
	isFirstList?: boolean;
	isBacklogCardList?: boolean;
};

const StyledTypeSelect = forwardRef((props: TypeSelectProps, ref: ForwardedRef<TypeSelectRef>) => {
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);

	return (
		<TypeSelectWrapper
			shouldShowChevron={isCompanyManaged}
			isVisualRefresh={isVisualRefreshEnabled()}
		>
			<TypeSelect {...props} ref={ref} />
		</TypeSelectWrapper>
	);
});

const localStorage = createLocalStorageProvider('backlog');
const SELECTED_ISSUE_TYPE_KEY = 'selectedIssueType';

const InlineCardCreate = ({
	isBetweenCards = false,
	isOpenByDefault = false,
	messages,
	insertBefore = null,
	listId,
	placeholderText = '',
	triggerLabel = '',
	ariaLabelledBy,
	isFormVisible: isFormVisibleProp,
	onSetAiWorkCreatePanelSubject,
	isFocusMovedToAiPanel,
	isFirstList,
	isBacklogCardList,
}: Props) => {
	const { formatMessage } = useIntl();
	const initialCardTypes = useBacklogSelector(getCardTypesForICC);

	const parentId = useBacklogSelector(getLastAppliedParentFilters);
	const manageIssueTypesLink = useBacklogSelector((state) => {
		if (canEditBoardConfig(state)) {
			return manageIssueTypesSelector(state);
		}
		return null;
	});
	const enabledFilters = useBacklogSelector(getFilters);
	const hasICCOpen = useBacklogSelector((state) =>
		insertBefore != null ? hasICCOpenSelector(state, { cardId: insertBefore }) : false,
	);
	const issueParents = useBacklogSelector((state) => issueParentsSelector(state));
	const dispatch = useBacklogDispatch();

	const { storeFieldsFromCardCreateData, storeFieldsFromFetchIssuesData } = useStoreFields({
		issueParents,
	});
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);
	const isBoardCrossProject = useBacklogSelector(getIsBoardCrossProject);
	const inlineCreateSessionId = useBacklogSelector(getInlineCreateSessionId);
	const defaultCardTypeId = localStorage.get(SELECTED_ISSUE_TYPE_KEY) || null;
	const hasEditBoardConfigPermission = useBacklogSelector((state) => canEditBoardConfig(state));

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		SmartLinkEditorEntryPointComponent,
		{},
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	// @deprecated Use `source` from `useAiWorkCreatePanelEntryPoint` instead
	const [accessibleConfluenceLink, setAccessibleConfluenceLink] = useState<string | undefined>(
		undefined,
	);

	let isAiEnabled = false;
	if (fg('ai_powered_backlog_inline_create_gate')) {
		if (fg('crocs-load_ai_enablement_in_backlog_router')) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			isAiEnabled = Boolean(useAiEntitlementResource()?.data?.isAiEnabledForJiraSoftware);
		} else {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			isAiEnabled = useIsAiEnabledBacklog();
		}
	}
	const shouldShowAiBacklogInlineCreate =
		isAiEnabled &&
		!isBetweenCards &&
		fg('ai_powered_backlog_inline_create_gate') &&
		// enable to BACKLOG card list, or SPRINT card list when experiment is on
		(listId === 'BACKLOG' ||
			(listId !== 'BOARD' &&
				expValEquals('ai_powered_backlog_inline_create_iter2', 'isEnabled', true, false)));

	const [isPlaceholderLinkPickerOpen, setIsPlaceholderLinkPickerOpen] = useState(false);
	const editorApiRef = useRef<EditorApi | null>(null);

	// When the experiment is on, we need to manually manage the form visibility state with callbacks from the hook
	// When the experiment is off, we fallback to existing behavior (isFormVisibleProp) and callbacks (showForm etc) don't do anything
	const { isFormVisible, showForm, hideForm, shouldSkipFormCloseOnBlur } = fg(
		'ai_powered_backlog_inline_create_gate',
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsFormVisible({
				isFocusMovedToAiPanel,
				isPlaceholderLinkPickerOpen,
				isFormVisibleOverride: isFormVisibleProp,
				isFormVisibleByDefault: isOpenByDefault || hasICCOpen,
			})
		: {
				isFormVisible: isFormVisibleProp,
				showForm: noop,
				hideForm: noop,
				shouldSkipFormCloseOnBlur: () => false,
			};

	const {
		selectedIssueType: selectedIssueTypeForAiPanel,
		onChangeIssueType: onChangeIssueTypeForAiPanel,
	} = fg('ai_powered_backlog_inline_create_gate') // eslint-disable-next-line react-hooks/rules-of-hooks
		? useSelectedIssueType({
				defaultCardTypeId,
				initialCardTypes,
				filteredIssueTypes: enabledFilters?.ISSUE_TYPE,
			})
		: { selectedIssueType: initialCardTypes[0], onChangeIssueType: noop };

	const {
		unloadAiWorkCreatePanel,
		loadAndShowAiWorkCreatePanel,
		loadAndShowAiWorkCreatePanelForSource,
		preloadAiWorkCreatePanel,
	} = fg('ai_powered_backlog_inline_create_gate')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useAiWorkCreatePanelEntryPoint({
				selectedIssueType: selectedIssueTypeForAiPanel,
				sourceLink: accessibleConfluenceLink,
				cardListId: listId,
				onSetAiWorkCreatePanelSubject,
				onAllSuggestionsActioned: editorApiRef.current?.clearContent,
			})
		: {
				unloadAiWorkCreatePanel: noop,
				loadAndShowAiWorkCreatePanel: noop,
				preloadAiWorkCreatePanel: noop,
				loadAndShowAiWorkCreatePanelForSource: noop,
			};

	useEffect(() => {
		if (
			!isFormVisible &&
			shouldShowAiBacklogInlineCreate &&
			expValEquals('ai_powered_backlog_inline_create_iter2', 'isEnabled', false, false) // not in Test cohort
		) {
			unloadAiWorkCreatePanel();
		}
	}, [unloadAiWorkCreatePanel, isFormVisible, shouldShowAiBacklogInlineCreate]);

	const onAutoOpen = useCallback(
		(sessionId: string) => {
			// start event for auto open icc
			const startAutoOpenICCAnalyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});

			// firing start event for icc that is auto open - same as onTriggerClickCallback
			// @ts-expect-error Second argument should be of format "actionSubject action"
			fireUIAnalytics(startAutoOpenICCAnalyticsEvent, 'triggerClicked', 'triggerButton', {
				formWasOpen: isFormVisible,
				triggerAppearance: isBetweenCards ? HIDDEN : VISIBLE,
				sessionId,
				isAutoOpen: true,
			});

			shouldShowAiBacklogInlineCreate && entryPointActions.load();

			dispatch(
				setInlineCreateSessionId({
					sessionId,
				}),
			);
		},
		[
			createAnalyticsEvent,
			dispatch,
			entryPointActions,
			isBetweenCards,
			isFormVisible,
			shouldShowAiBacklogInlineCreate,
		],
	);

	const onTriggerClick = (sessionId?: string) => {
		const shouldOpenGICModal =
			isCompanyManaged && (isBoardCrossProject || !initialCardTypes?.length);
		if (shouldOpenGICModal) {
			const fixVersions = enabledFilters.VERSION || [];
			const cardTypeId = initialCardTypes?.[0]?.id ?? null;
			dispatch(
				openModal({
					type: GlobalIssueCreateType,
					payload: {
						cardListId: listId,
						cardParentId: parentId,
						cardSummary: '',
						cardTypeId,
						rankBeforeCardId: insertBefore,
						fixVersions,
						sessionId: inlineCreateSessionId,
					},
				}),
			);
		} else {
			// Dispatch session id.
			if (sessionId) {
				dispatch(
					setInlineCreateSessionId({
						sessionId,
					}),
				);
			}
			shouldShowAiBacklogInlineCreate && entryPointActions.load();

			dispatch(setCardIdWithAboveICCOpen(insertBefore));
		}

		showForm();
	};

	const onCancel = useCallback(() => {
		dispatch(setCardIdWithAboveICCOpen(null));
	}, [dispatch]);

	const onCreate = useCallback(
		(
			inlineCards: {
				summary: string;
				cardTypeId: CardTypeId;
			}[],
		) => {
			const { cardTypeId } = inlineCards[0];
			const cardTypeEntity = initialCardTypes.find((ct) => ct.id === cardTypeId);
			const fixVersions = enabledFilters.VERSION || [];
			const tempIds = inlineCards.map(() => generateId());

			const doesCardTypeHaveRequiredFields = !!cardTypeEntity?.hasRequiredFields;

			if (doesCardTypeHaveRequiredFields) {
				dispatch(
					openModal({
						type: GlobalIssueCreateType,
						payload: {
							cardListId: listId,
							cardParentId: parentId,
							// With requiredfield multiple card creation is disabled
							cardSummary: inlineCards[0].summary,
							cardTypeId: Number(cardTypeId),
							rankBeforeCardId: insertBefore,
							fixVersions,
							sessionId: inlineCreateSessionId,
						},
					}),
				);
				return;
			}

			/** Analytics: We indicate to the analytics for issue creation that the inline creation ended up finishing inline creation (With session ID) */
			const analyticsEvent = createAnalyticsEvent({
				action: 'created',
				actionSubject: 'issue',
				sessionId: inlineCreateSessionId,
			});

			dispatch(
				cardCreateMultipleRequest({
					tempIds,
					summaries: inlineCards.map((inlineCard) => inlineCard.summary),
					typeId: Number(cardTypeId),
					typeIcon: cardTypeEntity ? cardTypeEntity.iconUrl : '',
					parentId,
					fixVersions,
					insertBefore,
					listId,
					storeFieldsFromCardCreateData,
					storeFieldsFromFetchIssuesData,
					analyticsEvent,
				}),
			);

			localStorage.set(SELECTED_ISSUE_TYPE_KEY, cardTypeId);
		},
		[
			initialCardTypes,
			enabledFilters.VERSION,
			createAnalyticsEvent,
			dispatch,
			parentId,
			insertBefore,
			listId,
			storeFieldsFromCardCreateData,
			storeFieldsFromFetchIssuesData,
			inlineCreateSessionId,
		],
	);

	const onBlurWrapper = useCallback(() => {
		// skip only if the experiment is on
		if (shouldShowAiBacklogInlineCreate && shouldSkipFormCloseOnBlur()) {
			return;
		}
		hideForm();
		onCancel();
	}, [shouldSkipFormCloseOnBlur, shouldShowAiBacklogInlineCreate, hideForm, onCancel]);

	const onCancelWrapper = useCallback(() => {
		hideForm();
		onCancel();
	}, [onCancel, hideForm]);

	const onIccCreate = useCallback(
		(
			issues: {
				summary: string;
				cardTypeId: number | string;
			}[],
		) => {
			onCreate(
				issues.map((issue) => ({
					summary: issue.summary,
					cardTypeId: Number(issue.cardTypeId),
				})),
			);
		},
		[onCreate],
	);

	const isFormHidden = isCompanyManaged && (isBoardCrossProject || !initialCardTypes?.length);

	const [showAiSuggestButton, setShowAiSuggestButton] = useState(false);
	const [editorDocCardState, setEditorDocCardState] = useState<JSONDocNode | undefined>(undefined);

	// @deprecated: being replaced by onSuggestFromSource; used in iter1 flow only
	const onSuggest = loadAndShowAiWorkCreatePanel;
	const onSuggestFromSource = useCallback(
		(sourceLink: string) => {
			loadAndShowAiWorkCreatePanelForSource(sourceLink);
			onCancelWrapper();
		},
		[loadAndShowAiWorkCreatePanelForSource, onCancelWrapper],
	);

	const preload = () => entryPointActions.preload();
	const cancelPreload = () => entryPointActions.cancelPreload();

	// Lazy loaded for performance, HoverBox contains preload to load editor on intent
	const renderSmartLinkEditor = useCallback(
		(props: {
			value: string;
			onChange: (value: string) => void;
			onSubmit: () => void;
			onCancel: () => void;
			dataTestId: string;
		}) => {
			return (
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="smartLinkEditor"
					packageName="backlog"
					errorFallback="unmount"
					teamName="Crocs"
					runtimeProps={{
						...props,
						accessibleConfluenceLink,
						setAccessibleConfluenceLink,
						setShowAiSuggestButton,
						setIsLinkPickerOpen: setIsPlaceholderLinkPickerOpen,
						setEditorDocCardState,
						showAiSuggestButton,
						onSuggest,
						editorDocCardState,
						isLinkPickerOpen: isPlaceholderLinkPickerOpen,
						editorApiRef,
					}}
				/>
			);
		},
		[
			entryPointReferenceSubject,
			accessibleConfluenceLink,
			showAiSuggestButton,
			onSuggest,
			editorDocCardState,
			isPlaceholderLinkPickerOpen,
		],
	);

	const renderPlaceholder = useCallback(
		() => (
			<PlaceholderWithLinkPicker
				onLinkSelected={onSuggestFromSource}
				setIsLinkPickerOpen={setIsPlaceholderLinkPickerOpen}
				isLinkPickerOpen={isPlaceholderLinkPickerOpen}
			/>
		),
		[isPlaceholderLinkPickerOpen, onSuggestFromSource],
	);

	const renderAiSuggestButton = useCallback(() => {
		return showAiSuggestButton && <AiSuggestButton onClick={onSuggest} />;
	}, [onSuggest, showAiSuggestButton]);

	let nudgeId = '';
	const isEligibleIccForBacklogCreateSprintIssueNudge =
		isTailoredOnboardingScrumAndSoftwareKanbanEnabled() &&
		isFirstList &&
		!isBacklogCardList &&
		!isBetweenCards;
	const isEligibleIccForBacklogCreateIssueNudge =
		isTailoredOnboardingScrumAndSoftwareKanbanEnabled() && isBacklogCardList && !isBetweenCards;

	if (isEligibleIccForBacklogCreateIssueNudge) {
		nudgeId = BACKLOG_CREATE_ISSUE_NUDGE_ID;
	} else if (isEligibleIccForBacklogCreateSprintIssueNudge) {
		nudgeId = BACKLOG_CREATE_SPRINT_ISSUE_NUDGE_ID;
	}

	const { ref: iccContainerRef } = useRegisterNudgeTarget<HTMLDivElement>(
		nudgeId,
		// Only register if we're in a valid InlineCardCreate for one of the nudge targets
		isTailoredOnboardingScrumAndSoftwareKanbanEnabled() && Boolean(nudgeId),
	);
	const showIssueTypeConfigOptions = !isCompanyManaged && hasEditBoardConfigPermission;

	const shouldLoadICCNudge = !isCompanyManaged || !fg('load_icc_nudge_conditionally_on_backlog');

	/**
	 * When ai_powered_backlog_inline_create_gate is OFF, don't return any values
	 * When ai_powered_backlog_inline_create_gate is ON:
	 * - ai_powered_backlog_inline_create_iter2 is TEST show placeholder experiment
	 * - ai_powered_backlog_inline_create_iter2 is CONTROL show original editor experiment
	 */
	const getExperimentalAiValues = () => {
		if (shouldShowAiBacklogInlineCreate) {
			return expValEquals('ai_powered_backlog_inline_create_iter2', 'isEnabled', true, false)
				? { renderCompactViewPlaceholderExperimental: renderPlaceholder }
				: {
						renderCompactViewEditorExperimental: renderSmartLinkEditor,
						renderCompactViewActionButtonExperimental: renderAiSuggestButton,
					};
		}
		return undefined;
	};

	return (
		<>
			{isEligibleIccForBacklogCreateIssueNudge && shouldLoadICCNudge && (
				<BacklogCreateIssueNudgePortalledAsync />
			)}
			{isEligibleIccForBacklogCreateSprintIssueNudge && shouldLoadICCNudge && (
				<BacklogCreateSprintIssueNudgeAsync />
			)}
			<HoverBox
				onMouseEnter={preload}
				onMouseLeave={cancelPreload}
				shouldRenderHoverBox={shouldShowAiBacklogInlineCreate}
				onLinkPicked={onSuggestFromSource}
				onLinkPickerMounted={preloadAiWorkCreatePanel}
			>
				<PlatformInlineIssueCreate
					cardTypes={initialCardTypes}
					extraItems={
						(manageIssueTypesLink && [
							{
								url: manageIssueTypesLink,
							},
						]) ||
						undefined
					}
					numFilters={Object.keys(enabledFilters).length}
					onCreate={onIccCreate}
					onTriggerClick={onTriggerClick}
					onCancel={onCancelWrapper}
					onBlur={onBlurWrapper}
					appearance={COMPACT}
					triggerAppearance={isBetweenCards ? HIDDEN : VISIBLE}
					isFormVisibleDefault={isFormHidden ? false : isOpenByDefault || hasICCOpen}
					messages={messages}
					placeholderText={placeholderText}
					triggerLabel={
						shouldShowAiBacklogInlineCreate &&
						(!fg('ai_powered_backlog_inline_create-create_in_sprint') || isBacklogCardList)
							? formatMessage(formatMessages.triggerLabelForAiWorkCreate)
							: triggerLabel
					}
					ariaLabelledBy={ariaLabelledBy}
					defaultCardTypeId={defaultCardTypeId}
					isFormVisible={isFormHidden ? false : isFormVisible}
					TypeSelect={StyledTypeSelect}
					onAutoOpen={onAutoOpen}
					onChangeCardType={onChangeIssueTypeForAiPanel}
					showIssueTypeConfigOptions={showIssueTypeConfigOptions}
					configSource={JSW_BACKLOG_SOURCE}
					containerRef={
						isTailoredOnboardingScrumAndSoftwareKanbanEnabled() ? iccContainerRef : undefined
					}
					{...getExperimentalAiValues()}
				/>
			</HoverBox>
		</>
	);
};

export default InlineCardCreate;
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TypeSelectWrapper = styled.div<{
	shouldShowChevron: boolean;
	isVisualRefresh: boolean;
}>({
	// Spacing tokens are in use
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: ({ shouldShowChevron, isVisualRefresh }) =>
		// eslint-disable-next-line no-nested-ternary
		shouldShowChevron
			? token('space.500')
			: isVisualRefresh
				? token('space.100')
				: token('space.250'),
	borderLeft: '1px solid transparent', // this is used to match with the border of the card, we can set margin-left to 21px, but we won't be able to migrate to use the space token
});
